import { dashboard as Api, type DashboardWeek } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { SkeletonPage } from '../components/Shimmer';
import { Stat } from '../components/Stat';

const inr = (n: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
/* A level that was never set is "—", not 0: a stop-loss of zero is a
   different instruction entirely. */
const price = (n: number | null) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

export function Dashboard() {
  const { data, error, loading, reload } = useAsync((signal) => Api.get(signal));

  if (loading && !data) return <SkeletonPage />;
  if (error) {
    return (
      <div className="pg">
        <div className="err">
          Could not load the dashboard — {error.message}
          <button className="b bo" style={{ marginLeft: 12 }} onClick={() => void reload()}>Try again</button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { members, mrr, planMix, renewalsDue, failed, weeks, openCalls, stats90, todayNote } = data;
  /* A switched-off module's figures arrive as null and are left out. */
  const nothingYet = members.total === 0 && (openCalls?.length ?? 0) === 0;

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Overview</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            {openCalls ? (openCalls.length ? ` · ${openCalls.length} call${openCalls.length === 1 ? '' : 's'} open` : ' · no calls open') : ''}
            {data.modules?.news === false ? '' : (todayNote ? ` · morning note published` : ' · no note published yet')}
          </p>
        </div>
      </div>

      {nothingYet ? (
        <div className="crd" style={{ padding: '30px 32px', marginBottom: 18, borderColor: 'rgba(var(--goldrgb), .28)' }}>
          <div style={{ fontFamily: 'var(--fd)', fontSize: 19, marginBottom: 8 }}>Nothing has been published yet</div>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', lineHeight: 1.6, maxWidth: 620 }}>
            These figures fill in as the desk works: members as people register, revenue as
            payments are recorded, the chart as subscriptions come in. Until then they read
            “—”, because a zero here would look like a measurement.
          </p>
        </div>
      ) : null}

      <div className="dash-stats-grid" style={{ marginBottom: 18 }}>
        <Stat label="Active members" value={members.total ? members.total.toLocaleString('en-IN') : null}
          hint="Nobody has registered yet"
          note={`${members.paying} paying`} />
        <Stat label="Paying subscribers" value={members.paying ? members.paying.toLocaleString('en-IN') : null}
          hint="No paid plans in force"
          note={members.total ? `${Math.round((members.paying / members.total) * 100)}% of accounts` : null} />
        <Stat label="MRR" value={mrr ? inr(mrr) : null}
          hint="Publish plans with prices to see this"
          note="From active members' plans" />
        <Stat label="Renewals due · 7 days" value={members.total ? String(renewalsDue) : null}
          hint="No memberships to renew"
          note={failed ? <span style={{ color: 'var(--down)' }}>{failed} payment failed</span> : 'none failed'} />
      </div>

      <div className="dash-split">
        <div className="crd" style={{ padding: '24px 26px' }}>
          <div className="kh">New paid subscriptions · last 8 weeks</div>
          <WeeklyChart weeks={weeks} />
        </div>

        <div className="crd" style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column' }}>
          <div>
            <div className="kh" style={{ marginBottom: 14 }}>Plan mix</div>
            {planMix.length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>No members yet.</p>
            ) : (
              <table className="tbl">
                <tbody>
                  {planMix.map((p) => (
                    <tr key={p.plan}>
                      <td>{p.plan}</td>
                      <td className="right">{p.n.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {stats90 && stats90.closedCount > 0 ? (
            <p style={{ fontSize: 11.5, color: 'var(--tx3)', marginTop: 'auto', paddingTop: 14 }}>
              {stats90.hitRate}% of {stats90.closedCount} closed calls hit target · 90 days
            </p>
          ) : null}
        </div>
      </div>

      {openCalls ? (
      <div className="crd" style={{ padding: '24px 26px', marginTop: 18 }}>
        <div className="rowbar" style={{ marginBottom: 12 }}>
          <div className="kh">Open trade calls</div>
          <a className="b bo" href="/content/trade-calls/new/">Manage calls ↗</a>
        </div>
        {openCalls.length === 0 ? (
          <p className="muted" style={{ fontSize: 13 }}>No calls are open. Published calls appear here until they are closed.</p>
        ) : (
          <div className="tblx">
            <table className="tbl">
              <thead>
                <tr><th>Stock</th><th>Side</th><th>Entry</th><th>Target</th><th>SL</th><th>Posted</th></tr>
              </thead>
              <tbody>
                {openCalls.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 700 }}>{c.instrument}</td>
                    <td>{c.side}</td>
                    <td>{price(c.entry)}</td>
                    <td>{price(c.target)}</td>
                    <td>{price(c.stop_loss)}</td>
                    <td className="muted">{new Date(c.created_at.replace(' ', 'T') + 'Z')
                      .toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      ) : null}
    </div>
  );
}

/** Bars, drawn from the weeks the API returned — including the empty ones. */
function WeeklyChart({ weeks }: { weeks: DashboardWeek[] }) {
  const max = Math.max(1, ...weeks.map((w) => w.total));
  const any = weeks.some((w) => w.total > 0);
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 160 }}>
        {weeks.map((w, i) => (
          <div key={w.week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div title={`${w.total} in week ${i + 1}`} style={{
              width: '100%',
              height: `${any ? Math.max(2, (w.total / max) * 130) : 2}px`,
              background: w.total ? 'var(--gold)' : 'rgba(var(--wash), calc(.07 * var(--washx)))',
              borderRadius: '6px 6px 0 0',
            }} />
            <span style={{ fontSize: 10, color: 'var(--tx3)' }}>W{i + 1}</span>
          </div>
        ))}
      </div>
      {!any ? (
        <p className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
          No paid subscriptions recorded in the last eight weeks.
        </p>
      ) : null}
    </div>
  );
}
